import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, Coins, Wrench, Plus, Minus, Loader2, Check, X,
    Building2, Mail, Phone, Receipt, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin → Gebruiker detail: bekijk balans + verbruik + totaal kosten,
 * ken credits toe (+ / −) en activeer/deactiveer tools per makelaar.
 */
export default function AdminUserDetail() {
    const { userId } = useParams();
    const [data, setData] = useState(null);
    const [history, setHistory] = useState([]);
    const [catalogue, setCatalogue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [grantAmount, setGrantAmount] = useState("");
    const [grantNote, setGrantNote] = useState("");
    const [granting, setGranting] = useState(false);
    const [savingTool, setSavingTool] = useState(null);
    const [savingLang, setSavingLang] = useState(false);

    const toggleMails = async () => {
        const enable = !!data.user.mails_disabled;
        try {
            await api.put(`/admin/users/${userId}/mails-enabled`, { enabled: enable });
            toast.success(enable ? "Mails weer ingeschakeld" : "Alle automatische mails uitgezet voor deze gebruiker");
            await refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fout");
        }
    };

    const setLanguage = async (lang) => {
        setSavingLang(true);
        try {
            const { data: res } = await api.put(`/admin/users/${userId}/language`, { preferred_language: lang });
            toast.success(res.rerendered_drafts > 0
                ? `Taal ingesteld op ${lang.toUpperCase()} · ${res.rerendered_drafts} concept-mail(s) opnieuw gemaakt in die taal`
                : `Taal ingesteld op ${lang.toUpperCase()} — alle nieuwe mails volgen deze taal`);
            await refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Taal instellen mislukt");
        }
        setSavingLang(false);
    };

    const refresh = React.useCallback(async () => {
        try {
            const [u, h, c] = await Promise.all([
                api.get(`/admin/users/${userId}/tools`),
                api.get(`/admin/users/${userId}/credits/history`),
                api.get(`/admin/tools/catalogue`),
            ]);
            setData(u.data);
            setHistory(h.data.transactions || []);
            setCatalogue(c.data.tools || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kon gebruiker niet laden");
        }
        setLoading(false);
    }, [userId]);

    const cancelDeletion = async () => {
        try {
            await api.post(`/admin/users/${userId}/cancel-deletion`);
            toast.success("Verwijdering geannuleerd — account blijft actief");
            refresh();
        } catch {
            toast.error("Annuleren mislukt");
        }
    };

    useEffect(() => {
        refresh();
    }, [refresh]);

    const grant = async (signMultiplier = 1) => {
        const n = parseInt(grantAmount, 10);
        if (!n || isNaN(n)) {
            toast.error("Geef een getal in");
            return;
        }
        setGranting(true);
        try {
            await api.post(`/admin/users/${userId}/credits/grant`, {
                amount: signMultiplier * Math.abs(n),
                note: grantNote || null,
            });
            toast.success(signMultiplier > 0 ? `+${n} credits toegekend` : `-${n} credits afgetrokken`);
            setGrantAmount("");
            setGrantNote("");
            await refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Mislukt");
        }
        setGranting(false);
    };

    const toggleTool = async (toolKey, enabled) => {
        setSavingTool(toolKey);
        try {
            await api.patch(`/admin/users/${userId}/tools`, { tool_key: toolKey, enabled });
            await refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kon niet opslaan");
        }
        setSavingTool(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-zinc-50 text-zinc-950 p-10">
                <p className="text-sm text-zinc-500">Laden…</p>
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="admin-user-detail-page">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
                <Link
                    to="/admin/users"
                    className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-8"
                    data-testid="user-back-link"
                >
                    <ArrowLeft className="h-4 w-4" /> Makelaars
                </Link>

                {/* GDPR deletion warning */}
                {data.user.deletion_scheduled_at && (
                    <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="deletion-warning">
                        <p className="text-sm text-red-700 font-medium">
                            ⚠️ Deze gebruiker vroeg accountverwijdering aan — wordt definitief verwijderd op {new Date(data.user.deletion_scheduled_at).toLocaleDateString("nl-BE")}.
                        </p>
                        <Button onClick={cancelDeletion} className="h-9 px-4 bg-white border border-red-300 text-red-700 hover:bg-red-100 rounded-sm text-sm font-bold" data-testid="cancel-deletion-btn">
                            Verwijdering annuleren
                        </Button>
                    </div>
                )}

                {/* Header card */}
                <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Makelaar</p>
                            <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="user-detail-name">
                                {data.user.name}
                            </h1>
                            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                                <span className="inline-flex items-center gap-1.5"><Mail className="h-3 w-3" /> {data.user.email}</span>
                                {data.user.company && (
                                    <span className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3" /> {data.user.company}</span>
                                )}
                            </div>
                            {data.user.signup_utm?.utm_source && (
                                <div className="mt-2 flex items-center gap-2 text-xs" data-testid="user-signup-source">
                                    <span className="text-[11px] uppercase tracking-wider text-zinc-500">Herkomst:</span>
                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-sm font-bold" title={data.user.signup_utm.referrer || ""}>
                                        {data.user.signup_utm.utm_source}
                                        {data.user.signup_utm.utm_medium ? ` · ${data.user.signup_utm.utm_medium}` : ""}
                                        {data.user.signup_utm.utm_campaign ? ` · ${data.user.signup_utm.utm_campaign}` : ""}
                                    </span>
                                </div>
                            )}
                            <div className="mt-3 flex items-center gap-2" data-testid="user-language-row">
                                <span className="text-[11px] uppercase tracking-wider text-zinc-500">Mailtaal:</span>
                                {[["nl", "🇧🇪 NL"], ["fr", "🇫🇷 FR"], ["en", "🇬🇧 EN"], ["es", "🇪🇸 ES"]].map(([l, label]) => (
                                    <button key={l} onClick={() => setLanguage(l)} disabled={savingLang}
                                        className={`h-7 px-2.5 text-[11px] font-bold rounded-sm border transition ${(data.user.preferred_language || "nl") === l ? "bg-[#E5A93D] text-black border-[#E5A93D]" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"}`}
                                        data-testid={`user-language-${l}`}>
                                        {label}
                                    </button>
                                ))}
                                <span className="mx-1 text-zinc-300">|</span>
                                <button onClick={toggleMails}
                                    className={`h-7 px-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold rounded-sm border transition ${data.user.mails_disabled ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"}`}
                                    title={data.user.mails_disabled ? "Deze gebruiker ontvangt géén automatische mails. Klik om weer in te schakelen." : "Zet alle automatische mails (herinneringen, reeks, systeemmails) uit voor deze gebruiker."}
                                    data-testid="user-mails-toggle">
                                    {data.user.mails_disabled ? "🔕 Mails uit" : "🔔 Mails aan"}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-6">
                            <Stat
                                label="Balans"
                                value={data.credits_balance}
                                accent="#22c55e"
                                testId="user-credits-balance"
                            />
                            <Stat
                                label="Verbruikt"
                                value={data.credits_total_used}
                                accent="#E5A93D"
                                testId="user-credits-used"
                            />
                            <Stat
                                label="Totaal kosten"
                                value={`€ ${(data.tools_total_cost_eur || 0).toFixed(2)}`}
                                accent="#7C3AED"
                                testId="user-total-cost"
                            />
                            <Link
                                to={`/tools/invoices?user_id=${userId}`}
                                data-testid="user-detail-invoices-link"
                                className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-200 text-sm text-zinc-800 hover:text-zinc-950 hover:bg-zinc-100 hover:border-zinc-400 transition"
                            >
                                <Receipt className="h-4 w-4 text-[#E5A93D]" />
                                Facturen
                            </Link>
                            <QueueWelcomeMailButton userId={userId} userName={data.name} />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
                    {/* Grant credits */}
                    <section className="bg-white border border-zinc-200 rounded-sm p-6">
                        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-4 inline-flex items-center gap-2">
                            <Coins className="h-3 w-3" /> Credits beheren
                        </p>
                        <div className="flex items-center gap-2 mb-3">
                            <Input
                                type="number"
                                value={grantAmount}
                                onChange={(e) => setGrantAmount(e.target.value)}
                                placeholder="Aantal credits"
                                data-testid="grant-amount-input"
                                className="bg-white border-zinc-200 h-10"
                            />
                            <Button
                                onClick={() => grant(1)}
                                disabled={granting || !grantAmount}
                                data-testid="grant-add-btn"
                                className="bg-[#22c55e] hover:bg-[#22c55e]/90 text-black font-medium rounded-sm h-10 px-4"
                            >
                                {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Toekennen</>}
                            </Button>
                            <Button
                                onClick={() => grant(-1)}
                                disabled={granting || !grantAmount}
                                data-testid="grant-deduct-btn"
                                variant="ghost"
                                className="rounded-sm h-10 px-3 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100 border border-zinc-200"
                            >
                                <Minus className="h-4 w-4" />
                            </Button>
                        </div>
                        <Input
                            value={grantNote}
                            onChange={(e) => setGrantNote(e.target.value)}
                            placeholder="Notitie (optioneel) — bv. 'Welkomstpakket'"
                            data-testid="grant-note-input"
                            className="bg-white border-zinc-200 h-9 text-xs"
                        />
                    </section>

                    {/* Tools access */}
                    <section className="bg-white border border-zinc-200 rounded-sm p-6">
                        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-4 inline-flex items-center gap-2">
                            <Wrench className="h-3 w-3" /> Tools toegang
                        </p>
                        <div className="space-y-3">
                            {catalogue.map((tool) => {
                                const enabled = !!(data.tool_access || {})[tool.key];
                                return (
                                    <div
                                        key={tool.key}
                                        className="flex items-center justify-between p-3 bg-white border border-zinc-200 rounded-sm"
                                        data-testid={`tool-access-row-${tool.key}`}
                                    >
                                        <div>
                                            <p className="text-sm text-zinc-950 font-medium">{tool.label}</p>
                                            <p className="text-xs text-zinc-500 mt-0.5">{tool.credit_cost} {tool.credit_cost === 1 ? "credit" : "credits"} per generatie</p>
                                        </div>
                                        <Switch
                                            checked={enabled}
                                            disabled={savingTool === tool.key}
                                            onCheckedChange={(checked) => toggleTool(tool.key, checked)}
                                            data-testid={`tool-access-toggle-${tool.key}`}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {/* History */}
                <section className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-4">Transactie historie</p>
                    {history.length === 0 ? (
                        <p className="text-sm text-zinc-500">Nog geen transacties.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                                    <tr>
                                        <th className="text-left py-2 pr-4">Datum</th>
                                        <th className="text-left py-2 pr-4">Type</th>
                                        <th className="text-right py-2 pr-4">Aantal</th>
                                        <th className="text-right py-2 pr-4">Balans erna</th>
                                        <th className="text-left py-2 pr-4">Tool</th>
                                        <th className="text-left py-2">Notitie</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-200">
                                    {history.map((t) => (
                                        <tr key={t.id} data-testid={`tx-row-${t.id}`}>
                                            <td className="py-2 pr-4 text-zinc-500 whitespace-nowrap">
                                                {new Date(t.created_at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" })}
                                            </td>
                                            <td className="py-2 pr-4">
                                                <TxBadge type={t.type} />
                                            </td>
                                            <td className={`py-2 pr-4 text-right tabular-nums font-medium ${t.amount > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                                                {t.amount > 0 ? "+" : ""}{t.amount}
                                            </td>
                                            <td className="py-2 pr-4 text-right tabular-nums text-zinc-600">{t.balance_after}</td>
                                            <td className="py-2 pr-4 text-zinc-500">
                                                {t.tool_key ? (catalogue.find((c) => c.key === t.tool_key)?.label || t.tool_key) : "—"}
                                            </td>
                                            <td className="py-2 text-zinc-500">{t.note || t.reason || ""}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function Stat({ label, value, accent, testId }) {
    return (
        <div data-testid={testId}>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
            <p className="text-2xl font-semibold tabular-nums leading-tight" style={{ color: accent }}>
                {value}
            </p>
        </div>
    );
}

function TxBadge({ type }) {
    const styles = {
        grant: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
        deduct: "bg-rose-500/15 text-rose-700 border-rose-500/30",
        spend: "bg-[#E5A93D]/15 text-[#E5A93D] border-[#E5A93D]/30",
        refund: "bg-zinc-100 text-zinc-600 border-zinc-300",
    };
    const labels = { grant: "Toekenning", deduct: "Aftrek", spend: "Verbruik", refund: "Terugbetaling" };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${styles[type] || styles.refund}`}>
            {labels[type] || type}
        </span>
    );
}


function QueueWelcomeMailButton({ userId, userName }) {
    const [loading, setLoading] = React.useState(false);
    const queue = async () => {
        setLoading(true);
        try {
            await api.post(`/admin/users/${userId}/queue-welcome-mail`);
            toast.success(`Welkomstmail voor ${userName || "gebruiker"} toegevoegd aan je Mails-queue`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kon mail niet aanmaken");
        } finally { setLoading(false); }
    };
    return (
        <button
            type="button"
            onClick={queue}
            disabled={loading}
            data-testid="user-detail-queue-welcome-mail"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-[#E5A93D]/40 text-sm text-[#E5A93D] hover:bg-[#E5A93D]/10 transition disabled:opacity-50"
            title="Maak een welkomstmail aan in /admin/mails — review en verstuur jij zelf"
        >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Welkomstmail klaarzetten
        </button>
    );
}
